<template>
  <!-- 日程统计：拜访/会议/产品学习等日程的数量、完成率与明细 -->
  <report-layout active="schedule">
    <list-page>
      <search-condition @search="resetSearch" :show-search="true" page-name="scheduleReport">
        <template #default>
          <yq-date-range v-model:start-time="query.startTime" v-model:end-time="query.endTime" class="double-width">
            <template #prefix>日程日期</template>
          </yq-date-range>
          <el-select v-model="query.calendarType" placeholder="全部" clearable>
            <template #prefix>日程类型</template>
            <el-option v-for="type in SCHEDULE_TYPES" :key="type" :label="typeName(type)" :value="type"/>
          </el-select>
          <employee-select v-model="query.employeeId" placeholder="全部" clearable>
            <template #prefix>负责人</template>
          </employee-select>
          <el-input v-model="query.searchKey" placeholder="请输入日程标题" clearable @keydown.enter="resetSearch">
            <template #prefix>日程标题</template>
          </el-input>
        </template>
        <template #option>
          <page-button @click="resetAll">重置</page-button>
          <page-button type="read" el-type="primary" @click="resetSearch">查询</page-button>
        </template>
      </search-condition>

      <list-table :show-shadow="false">
        <div v-loading="loading" class="report-body">
          <div class="stat-grid">
            <div class="stat-card">
              <div class="label">日程总数</div>
              <div class="value">{{ stat.totalCount || 0 }}<span class="unit">条</span></div>
              <div class="sub">筛选条件下的全部日程</div>
            </div>
            <div class="stat-card">
              <div class="label">本月日程</div>
              <div class="value">{{ stat.monthCount || 0 }}<span class="unit">条</span></div>
              <div class="sub">{{ currentMonth }}</div>
            </div>
            <div class="stat-card">
              <div class="label">完成率</div>
              <div class="value success">{{ stat.completionRate || 0 }}<span class="unit">%</span></div>
              <div class="sub">已完成日程 / 日程总数</div>
            </div>
          </div>

          <div class="grid-2">
            <div class="chart-card">
              <div class="section-title">日程类型分布</div>
              <div class="donut-wrap">
                <div ref="typeChart" class="donut-div"></div>
                <div class="donut-legend">
                  <div v-for="row in typeRows" :key="row.name" class="lg-row">
                    <span class="lg-dot" :style="{background: row.color}"></span>
                    <span class="lg-name">{{ row.name }}</span>
                    <span class="lg-val">{{ row.value }}</span>
                    <span class="lg-ratio">{{ row.ratio }}%</span>
                  </div>
                  <div v-if="!typeRows.length" class="lg-empty">暂无数据</div>
                </div>
              </div>
            </div>
            <div class="chart-card">
              <div class="section-title">日程月度趋势</div>
              <div ref="trendChart" class="chart-div"></div>
            </div>
          </div>

          <div class="chart-card">
            <div class="section-title">日程明细 <span class="muted small">（最近 100 条）</span></div>
            <el-table :data="stat.rows || []" border size="small" empty-text="暂无数据" max-height="420">
              <el-table-column label="日期" prop="goalDate" width="120" align="center">
                <template v-slot="{row}"><span class="num">{{ dateOnly(row.goalDate) || '—' }}</span></template>
              </el-table-column>
              <el-table-column label="日程标题" prop="title" min-width="220" show-overflow-tooltip>
                <template v-slot="{row}">{{ row.title || '—' }}</template>
              </el-table-column>
              <el-table-column label="类型" prop="calendarType" width="120" align="center">
                <template v-slot="{row}">
                  <el-tag size="small" :type="typeTag(row.calendarType)">{{ typeName(row.calendarType) }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="负责人" prop="employeeName" width="120">
                <template v-slot="{row}">{{ row.employeeName || '—' }}</template>
              </el-table-column>
              <el-table-column label="状态" prop="kpiStatus" width="110" align="center">
                <template v-slot="{row}">
                  <el-tag size="small" :type="statusTag(row.kpiStatus)">{{ statusName(row.kpiStatus) }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="备注" prop="remark" min-width="180" show-overflow-tooltip>
                <template v-slot="{row}">{{ row.remark || '—' }}</template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </list-table>
    </list-page>
  </report-layout>
</template>

<script>
import * as echarts from "echarts";
import {markRaw} from "vue";
import ReportLayout from "@/views/h5/crm/report/ReportLayout.vue";
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import YqDateRange from "@/components/h5/date/YqDateRange.vue";
import EmployeeSelect from "@/components/h5/data/EmployeeSelect.vue";
import calendarApi from "@/api/dz-crm/kpi/calendarApi";
import {SCHEDULE_TYPES, dateOnly, statusName, typeName} from "@/views/h5/crm/kpi/kpiCalendarDictionary";
import {REPORT_PALETTE, barOption, cleanParams, donutOption} from "@/views/h5/crm/report/reportUtil";

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default {
  name: "ScheduleReport",
  components: {ReportLayout, ListPage, ListTable, SearchCondition, PageButton, YqDateRange, EmployeeSelect},
  data() {
    const year = new Date().getFullYear();
    return {
      SCHEDULE_TYPES,
      loading: false,
      query: {startTime: `${year}-01-01`, endTime: today(), calendarType: "", employeeId: "", searchKey: ""},
      stat: {},
      typeChart: null,
      trendChart: null,
    };
  },
  computed: {
    currentMonth() {
      const now = new Date();
      return now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0");
    },
    typeRows() {
      return (this.stat.typeStats || []).map((row, index) => ({
        name: typeName(row.calendarType),
        value: row.count || 0,
        ratio: row.ratio || 0,
        color: REPORT_PALETTE[index % REPORT_PALETTE.length],
      }));
    },
  },
  beforeRouteLeave(to, from, next) {
    next();
  },
  mounted() {
    this.loadReport();
  },
  unmounted() {
    [this.typeChart, this.trendChart].forEach(chart => chart && chart.dispose());
  },
  methods: {
    typeName,
    statusName,
    dateOnly,
    async loadReport() {
      this.loading = true;
      try {
        const params = cleanParams({
          ...this.query,
          startTime: this.query.startTime ? this.query.startTime.slice(0, 10) : "",
          endTime: this.query.endTime ? this.query.endTime.slice(0, 10) : "",
        });
        this.stat = await calendarApi.scheduleStat(params) || {};
        this.$nextTick(() => this.renderCharts());
      } finally {
        this.loading = false;
      }
    },
    resetSearch() {
      this.loadReport();
    },
    resetAll() {
      const year = new Date().getFullYear();
      this.query = {startTime: `${year}-01-01`, endTime: today(), calendarType: "", employeeId: "", searchKey: ""};
      this.loadReport();
    },
    renderCharts() {
      this.typeChart = this.setOption(this.typeChart, this.$refs.typeChart,
          donutOption(this.typeRows, this.stat.totalCount || 0, "日程总数"));
      this.trendChart = this.setOption(this.trendChart, this.$refs.trendChart,
          barOption(this.stat.monthList || [], this.stat.countList || [], "#13b8b8"));
    },
    setOption(chart, dom, option) {
      if (!dom) {
        return chart;
      }
      if (!chart) {
        chart = markRaw(echarts.init(dom));
      }
      chart.setOption(option, true);
      return chart;
    },
    typeTag(type) {
      switch (type) {
        case 4: return "primary";
        case 5: return "success";
        case 6: return "warning";
        case 7: return "info";
        case 8: return "danger";
        default: return "info";
      }
    },
    statusTag(status) {
      if (Number(status) === 100) {
        return "success";
      }
      if (Number(status) === 50) {
        return "warning";
      }
      return "info";
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/views/h5/crm/report/report.scss";

.small {
  font-size: 12px;
  font-weight: 400;
}
</style>
